import { getEnv } from '@/utils/env';
import { getCurrentAccount, getAccountInfo } from './msal';
import { chatService } from './chatService';
import type {
  ConnectContact,
  ConnectCallback,
  ConnectAgent,
  ConnectAgentStateDefinition,
  ConnectGlobal,
  ConnectQueueEndpoint,
  ConnectChatController,
  ConnectConnection,
} from '@/types';
import {
  getHoldResumeConnection,
  getInitialOrCaptureContactId,
} from '@/components/amazon-connect/contactUtils';
import { findThirdPartyConnection } from '@/utils/transferConnectionUtils';
import { installStreamsLogFilter } from '@/connect/adapter/streamsLogFilter';
import 'amazon-connect-streams';
// ChatJS provides connect.ChatSession; Streams uses it for agent chat (getMediaController).
import 'amazon-connect-chatjs';

installStreamsLogFilter();

/** Normalize getEndpoints success callback payload; Streams may use endpoints, addresses, or PascalCase. */
function normalizeGetEndpointsResponse(data: unknown): ConnectQueueEndpoint[] {
  if (Array.isArray(data)) return data as ConnectQueueEndpoint[];
  if (data && typeof data === 'object') {
    const o = data as Record<string, unknown>;
    const arr =
      o.endpoints ?? o.addresses ?? o.Endpoints ?? o.Addresses ?? undefined;
    return Array.isArray(arr) ? (arr as ConnectQueueEndpoint[]) : [];
  }
  return [];
}

function addContactConnection(
  contact: ConnectContact,
  endpoint: ConnectQueueEndpoint,
  callbacks: ConnectCallback,
  options?: { destroyAgentConnectionOnSuccess?: boolean }
): void {
  if (typeof window === 'undefined' || !window.connect) {
    callbacks.failure?.(
      new Error('Amazon Connect Streams library not available')
    );
    return;
  }
  if (!contact.addConnection) {
    callbacks.failure?.(new Error('Add connection not available'));
    return;
  }
  // Must call on contact — unbound addConnection loses `this` (Streams uses this.getContactId).
  contact.addConnection(endpoint, {
    success: () => {
      if (!options?.destroyAgentConnectionOnSuccess) {
        callbacks.success?.();
        return;
      }
      const agentConnection = contact.getAgentConnection?.();
      if (!agentConnection?.destroy) {
        callbacks.failure?.(new Error('Agent connection not available'));
        return;
      }
      agentConnection.destroy(callbacks);
    },
    failure: callbacks.failure,
  });
}

/**
 * Connect voice-service error carries an exception name (e.g. in `.type`), but
 * the exact shape varies between versions, so match across every likely field
 * plus the serialized form.
 */
function containsExceptionName(err: unknown, name: string): boolean {
  if (!err) return false;
  try {
    const anyErr = err as {
      type?: unknown;
      name?: unknown;
      message?: unknown;
      stack?: unknown;
    };
    const haystack = [
      anyErr.type,
      anyErr.name,
      anyErr.message,
      anyErr.stack,
      typeof err === 'string' ? err : '',
      JSON.stringify(err),
    ]
      .filter(value => value != null)
      .join(' ');
    return haystack.includes(name);
  } catch {
    return false;
  }
}

function isCallAlreadyResumed(err?: Error): boolean {
  return containsExceptionName(err, 'CallAlreadyResumedException');
}

class AmazonConnectService {
  private monitorSessionContactId: string | null = null;
  private monitorTargetContactId: string | null = null;
  /** Set when OutboundDialPanel calls agent.connect(); cleared after the contact is seen. */
  private agentConnectOutboundPending = false;
  private agentConnectOutboundPendingTimer: ReturnType<
    typeof setTimeout
  > | null = null;
  private readonly agentConnectOutboundContactIds = new Set<string>();

  resetCCPSession(): void {
    this.clearAgentConnectOutboundTracking();
    this.clearExpectedMonitorSession();
    if (typeof window === 'undefined' || !window.connect) {
      return;
    }
    try {
      const connect = window.connect as unknown as {
        core?: {
          terminate?: () => void;
        };
      };
      connect.core?.terminate?.();
    } catch {
      // Ignore reset failures; a fresh initCCP call is still attempted.
    }
  }

  async getUserIdentity(): Promise<{ email: string; name: string } | null> {
    try {
      const account = await getCurrentAccount();
      if (!account) return null;

      const accountInfo = getAccountInfo(account);
      if (!accountInfo) return null;

      return {
        email: accountInfo.email,
        name: accountInfo.name,
      };
    } catch {
      return null;
    }
  }

  async initializeCCP(
    containerElement: HTMLElement,
    options?: {
      loginPopup?: boolean;
      loginOptions?: {
        autoClose?: boolean;
      };
      softphone?: {
        allowFramedSoftphone?: boolean;
      };
    }
  ): Promise<void> {
    const userIdentity = await this.getUserIdentity();
    if (!userIdentity) {
      throw new Error('User must be authenticated with Microsoft first');
    }

    if (typeof window === 'undefined' || !window.connect) {
      throw new Error(
        'Amazon Connect Streams library not available. Make sure amazon-connect-streams is installed.'
      );
    }

    const connect = window.connect as unknown as {
      core: {
        initCCP: (
          container: HTMLElement,
          options: {
            ccpUrl: string;
            region?: string;
            loginPopup?: boolean;
            loginOptions?: {
              autoClose?: boolean;
            };
            loginUrl?: string;
            softphone?: {
              allowFramedSoftphone?: boolean;
            };
          }
        ) => void;
      };
    };

    const existingIframe = containerElement.querySelector('iframe');
    if (existingIframe && existingIframe.parentNode === containerElement) {
      try {
        containerElement.removeChild(existingIframe);
      } catch {
        // Ignore
      }
    }

    const loginUrl = `${getEnv('VITE_AWS_ACCESS_PORTAL_URL')}/#/?tab=applications`;

    connect.core.initCCP(containerElement, {
      ccpUrl: getEnv('VITE_AMAZON_CONNECT_CCP_URL'),
      region: getEnv('VITE_AMAZON_CONNECT_REGION'),
      loginPopup: options?.loginPopup ?? true,
      loginOptions: options?.loginOptions ?? {
        autoClose: true,
      },
      loginUrl,
      softphone: {
        allowFramedSoftphone: options?.softphone?.allowFramedSoftphone ?? true,
      },
    });
  }

  /**
   * Reads connect.core.getEventBus() defensively. Streams only creates the
   * event bus inside initCCP; until then every connect.contact()/agent()/
   * onXxx() call throws "Cannot read properties of undefined (reading
   * 'subscribe')". Returns null when the bus is unavailable.
   */
  private getCcpEventBus(): unknown {
    try {
      const connect = (
        window as unknown as {
          connect?: { core?: { getEventBus?: () => unknown } };
        }
      ).connect;
      return connect?.core?.getEventBus?.() ?? null;
    } catch {
      return null;
    }
  }

  /**
   * Subscribes to a Streams event once the event bus is ready. When the bus
   * is missing (page load → initCCP window, CCP re-init, stale shared worker
   * after a deployment) the subscription is deferred and attached as soon as
   * the bus appears, instead of crashing with "reading 'subscribe'".
   * The returned unsubscribe works in both phases.
   */
  private subscribeSafely(
    subscribe: () => () => void,
    name: string,
    timeoutMs = 30_000
  ): () => void {
    if (this.getCcpEventBus()) {
      return subscribe();
    }

    console.warn(
      `[AC-Service] ${name}: CCP event bus not ready yet — deferring subscription`
    );
    let unsubscribe: (() => void) | null = null;
    let settled = false;
    const deadline = Date.now() + timeoutMs;

    const poll = () => {
      if (settled) return;
      if (this.getCcpEventBus()) {
        settled = true;
        clearInterval(timer);
        try {
          unsubscribe = subscribe();
          console.log(`[AC-Service] ${name}: deferred subscription attached`);
        } catch (error) {
          console.error(
            `[AC-Service] ${name}: deferred subscription failed`,
            error
          );
        }
        return;
      }
      if (Date.now() >= deadline) {
        settled = true;
        clearInterval(timer);
        console.error(
          `[AC-Service] ${name}: CCP event bus never became ready — subscription dropped`
        );
      }
    };

    const timer = setInterval(poll, 200);

    return () => {
      settled = true;
      clearInterval(timer);
      if (unsubscribe) {
        try {
          unsubscribe();
        } catch {
          /* ok */
        }
        unsubscribe = null;
      }
    };
  }

  onInitialized(callback: () => void): () => void {
    if (typeof window === 'undefined' || !window.connect) {
      throw new Error('Amazon Connect Streams library not available');
    }
    const connect = window.connect as unknown as {
      core: {
        onInitialized: (callback: () => void) => () => void;
      };
    };
    return this.subscribeSafely(
      () => connect.core.onInitialized(callback),
      'onInitialized'
    );
  }

  onAuthFail(callback: () => void): () => void {
    if (typeof window === 'undefined' || !window.connect) {
      throw new Error('Amazon Connect Streams library not available');
    }
    const connect = window.connect as unknown as {
      core: {
        onAuthFail: (callback: () => void) => () => void;
      };
    };
    return this.subscribeSafely(
      () => connect.core.onAuthFail(callback),
      'onAuthFail'
    );
  }

  onAuthorizeRetriesExhausted(callback: () => void): () => void {
    if (typeof window === 'undefined' || !window.connect) {
      throw new Error('Amazon Connect Streams library not available');
    }
    const connect = window.connect as unknown as {
      core: {
        onAuthorizeRetriesExhausted: (callback: () => void) => () => void;
      };
    };
    return this.subscribeSafely(
      () => connect.core.onAuthorizeRetriesExhausted(callback),
      'onAuthorizeRetriesExhausted'
    );
  }

  setAgentState(
    stateType: 'ONLINE' | 'OFFLINE' | 'ROUTABLE' | 'NOT_ROUTABLE',
    callbacks?: ConnectCallback
  ): void {
    const connect = window.connect as unknown as ConnectGlobal;

    const agent = new connect.Agent();
    const agentStates = agent.getAgentStates();
    const currentState = agent.getState();
    const ROUTABLE_TYPE = connect.AgentStateType.ROUTABLE;
    const NOT_ROUTABLE_TYPE = connect.AgentStateType.NOT_ROUTABLE;
    const OFFLINE_TYPE = connect.AgentStateType.OFFLINE;
    // Capture call site for debugging
    const stack = new Error().stack
      ?.split('\n')
      .slice(2, 5)
      .map(s => s.trim())
      .join(' ← ');
    console.log(
      '[AC-Service] setAgentState',
      JSON.stringify({
        target: stateType,
        currentStateName: currentState?.name,
        currentStateType: String(currentState?.type),
        enumValues: {
          ROUTABLE: String(ROUTABLE_TYPE),
          NOT_ROUTABLE: String(NOT_ROUTABLE_TYPE),
          OFFLINE: String(OFFLINE_TYPE),
        },
        availableStates: agentStates.map(s => ({
          name: s.name,
          type: String(s.type),
          arn: s.agentStateARN,
        })),
        callSite: stack,
      })
    );
    const targetState = agentStates.find(state => {
      switch (stateType) {
        case 'ONLINE':
        case 'ROUTABLE':
          return state.type === ROUTABLE_TYPE;
        case 'NOT_ROUTABLE':
          return state.type === NOT_ROUTABLE_TYPE;
        case 'OFFLINE':
          return state.type === OFFLINE_TYPE;
        default:
          return false;
      }
    });

    if (!targetState) {
      console.error(
        '[AC-Service] setAgentState -> target state not found',
        stateType
      );
      callbacks?.failure?.(new Error(`State ${stateType} not found`));
      return;
    }

    console.log(
      '[AC-Service] setAgentState -> calling agent.setState',
      targetState.name
    );
    agent.setState(targetState, {
      success: () => {
        console.log('[AC-Service] setAgentState -> SUCCESS', stateType);
        callbacks?.success?.();
      },
      failure: (err?: Error) => {
        console.error('[AC-Service] setAgentState -> FAILURE', stateType, err);
        callbacks?.failure?.(err);
      },
    });
  }

  /**
   * Set agent to offline (e.g. when closing the tab).
   * Uses enqueueNextState: true so the state change is queued and applied.
   * Call this from beforeunload/pagehide when the user leaves the app.
   */
  setAgentOffline(): Promise<void> {
    return new Promise((resolve, reject) => {
      if (typeof window === 'undefined' || !window.connect) {
        resolve();
        return;
      }
      const connect = window.connect as unknown as ConnectGlobal;
      const agent = new connect.Agent();
      const offlineState = agent
        .getAgentStates()
        .find(state => state.type === connect.AgentStateType.OFFLINE);
      if (!offlineState) {
        resolve();
        return;
      }
      agent.setState(
        offlineState,
        { success: () => resolve(), failure: reject },
        { enqueueNextState: true }
      );
    });
  }

  /**
   * Set agent state using a state definition from agent.getAgentStates().
   * Use this when displaying all CCP states in a dropdown or list.
   */
  setAgentStateByDefinition(
    stateDefinition: ConnectAgentStateDefinition,
    callbacks?: ConnectCallback
  ): void {
    if (typeof window === 'undefined' || !window.connect) {
      callbacks?.failure?.(
        new Error('Amazon Connect Streams library not available')
      );
      return;
    }
    const connect = window.connect as unknown as ConnectGlobal;
    const agent = new connect.Agent();
    agent.setState(stateDefinition, callbacks || {});
  }

  /**
   * Set agent state by its display name (e.g. "Forced Release").
   * Use this for custom statuses that must be selected by name rather than by
   * the generic NOT_ROUTABLE type, which may match a different not-routable state.
   */
  setAgentStateByName(name: string, callbacks?: ConnectCallback): void {
    if (typeof window === 'undefined' || !window.connect) {
      callbacks?.failure?.(
        new Error('Amazon Connect Streams library not available')
      );
      return;
    }
    const connect = window.connect as unknown as ConnectGlobal;
    const agent = new connect.Agent();
    const targetState = agent
      .getAgentStates()
      .find(state => state.name.toLowerCase() === name.toLowerCase());

    if (!targetState) {
      console.error(
        '[AC-Service] setAgentStateByName -> target state not found',
        name
      );
      callbacks?.failure?.(new Error(`State ${name} not found`));
      return;
    }

    console.log(
      '[AC-Service] setAgentStateByName -> calling agent.setState',
      targetState.name
    );
    agent.setState(targetState, {
      success: () => {
        console.log('[AC-Service] setAgentStateByName -> SUCCESS', name);
        callbacks?.success?.();
      },
      failure: (err?: Error) => {
        console.error('[AC-Service] setAgentStateByName -> FAILURE', name, err);
        callbacks?.failure?.(err);
      },
    });
  }

  acceptContact(contact: ConnectContact, callbacks?: ConnectCallback): void {
    contact.accept(callbacks || {});
  }

  /**
   * Returns the original contact id for a transfer chain when available
   * (see getInitialOrCaptureContactId), falling back to the current
   * contact id for non-transferred contacts.
   */
  getCallScopeContactId(contact: ConnectContact): string {
    const initialOrCaptureId = getInitialOrCaptureContactId(contact);
    if (initialOrCaptureId) {
      console.log('found initial or capture contact id', initialOrCaptureId);
      return initialOrCaptureId;
    }
    console.log(
      'no initial or capture contact id, using current contact id',
      contact.getContactId()
    );
    return contact.getContactId();
  }

  rejectContact(contact: ConnectContact, callbacks?: ConnectCallback): void {
    contact.reject(callbacks || {});
  }

  setExpectedMonitorSession(
    sessionContactId: string,
    targetContactId: string
  ): void {
    this.monitorSessionContactId = sessionContactId;
    this.monitorTargetContactId = targetContactId;
    console.log('[Connect][ExpectedMonitor] set', {
      sessionContactId,
      targetContactId,
    });
  }

  clearExpectedMonitorSession(): void {
    console.log('[Connect][ExpectedMonitor] clear', {
      previousSessionContactId: this.monitorSessionContactId,
      previousTargetContactId: this.monitorTargetContactId,
    });
    this.monitorSessionContactId = null;
    this.monitorTargetContactId = null;
  }

  /**
   * Mark that the next outbound voice contact comes from agent.connect() (dialpad).
   * Those contacts must not receive contact.accept() — Connect connects them automatically.
   */
  markAgentConnectOutboundDialStarted(): void {
    this.agentConnectOutboundPending = true;
    if (this.agentConnectOutboundPendingTimer) {
      clearTimeout(this.agentConnectOutboundPendingTimer);
    }
    this.agentConnectOutboundPendingTimer = setTimeout(() => {
      this.agentConnectOutboundPending = false;
      this.agentConnectOutboundPendingTimer = null;
    }, 60_000);
  }

  clearAgentConnectOutboundTracking(): void {
    this.agentConnectOutboundPending = false;
    if (this.agentConnectOutboundPendingTimer) {
      clearTimeout(this.agentConnectOutboundPendingTimer);
      this.agentConnectOutboundPendingTimer = null;
    }
    this.agentConnectOutboundContactIds.clear();
  }

  clearAgentConnectOutboundContact(contactId: string): void {
    this.agentConnectOutboundContactIds.delete(contactId);
  }

  /**
   * True for dialpad agent.connect() contacts only (not monitor join / other outbound).
   */
  isAgentConnectOutboundContact(contact: ConnectContact): boolean {
    try {
      const contactId = contact.getContactId();
      if (this.agentConnectOutboundContactIds.has(contactId)) {
        return true;
      }
      if (this.agentConnectOutboundPending) {
        this.agentConnectOutboundContactIds.add(contactId);
        this.agentConnectOutboundPending = false;
        if (this.agentConnectOutboundPendingTimer) {
          clearTimeout(this.agentConnectOutboundPendingTimer);
          this.agentConnectOutboundPendingTimer = null;
        }
        return true;
      }
    } catch {
      // ignore
    }
    return false;
  }

  isExpectedMonitorContact(contact: ConnectContact): boolean {
    try {
      // A monitor/barge contact carries a MONITORING connection. Detect it
      // directly so classification does not depend on in-memory session state:
      // on page reload (or when the contact arrives before setExpectedMonitor
      // runs) that state is empty, the contact is misclassified as voice, and
      // the machine opens GET /call/stream for the monitored contact — which
      // re-assigns the call/transcript to the monitor instead of the agent.
      if (this.hasMonitoringConnection(contact)) {
        return true;
      }
    } catch {
      /* fall through to id matching below */
    }

    if (!this.monitorSessionContactId && !this.monitorTargetContactId) {
      console.log('[Connect][ExpectedMonitor] no expected monitor session set');
      return false;
    }
    try {
      const contactId = contact.getContactId();
      console.log('[Connect][ExpectedMonitor] evaluating contact', {
        contactId,
        expectedSessionContactId: this.monitorSessionContactId,
        expectedTargetContactId: this.monitorTargetContactId,
      });
      if (
        this.monitorSessionContactId &&
        contactId === this.monitorSessionContactId
      ) {
        console.log('[Connect][ExpectedMonitor] matched by sessionContactId', {
          contactId,
          sessionContactId: this.monitorSessionContactId,
        });
        return true;
      }
      const initialOrCaptureId = getInitialOrCaptureContactId(contact);
      console.log('[Connect][ExpectedMonitor] initial contact check', {
        contactId,
        initialOrCaptureId,
        expectedTargetContactId: this.monitorTargetContactId,
      });
      const matchedByInitialContactId = Boolean(
        this.monitorTargetContactId &&
        initialOrCaptureId &&
        initialOrCaptureId === this.monitorTargetContactId
      );
      if (matchedByInitialContactId) {
        console.log('[Connect][ExpectedMonitor] matched by initialContactId', {
          contactId,
          initialOrCaptureId,
          targetContactId: this.monitorTargetContactId,
        });
      }
      if (!matchedByInitialContactId) {
        console.log('[Connect][ExpectedMonitor] not matched', {
          contactId,
          initialOrCaptureId,
          expectedSessionContactId: this.monitorSessionContactId,
          expectedTargetContactId: this.monitorTargetContactId,
        });
      }
      return matchedByInitialContactId;
    } catch (error) {
      console.log('[Connect][ExpectedMonitor] check failed', {
        error: error instanceof Error ? error.message : String(error),
      });
      return false;
    }
  }

  /** True when the contact has a connection of type MONITORING (supervisor monitor/barge). */
  private hasMonitoringConnection(contact: ConnectContact): boolean {
    if (typeof window === 'undefined' || !window.connect) return false;
    const connect = window.connect as unknown as {
      ConnectionType?: { MONITORING?: unknown };
    };
    const monitoringType = connect.ConnectionType?.MONITORING;
    return contact.getConnections().some(conn => {
      const type = conn.getType?.();
      return monitoringType !== undefined
        ? type === monitoringType
        : String(type).toLowerCase() === 'monitoring';
    });
  }

  onAgent(callback: (agent: unknown) => void): () => void {
    if (typeof window === 'undefined' || !window.connect) {
      throw new Error('Amazon Connect Streams library not available');
    }
    const connect = window.connect as unknown as {
      agent: (callback: (agent: unknown) => void) => () => void;
    };
    return this.subscribeSafely(() => connect.agent(callback), 'onAgent');
  }

  onContact(callback: (contact: unknown) => void): () => void {
    if (typeof window === 'undefined' || !window.connect) {
      throw new Error('Amazon Connect Streams library not available');
    }
    const connect = window.connect as unknown as {
      contact: (callback: (contact: unknown) => void) => () => void;
    };
    return this.subscribeSafely(() => connect.contact(callback), 'onContact');
  }

  /**
   * Returns media devices from the CCP iframe context.
   * Device IDs from this list must be used with agent.setSpeakerDevice() / setMicrophoneDevice(),
   * otherwise the CCP will reject them with "Failed to set speaker device".
   */
  getFrameMediaDevices(timeoutMs: number): Promise<MediaDeviceInfo[]> {
    if (typeof window === 'undefined' || !window.connect) {
      return Promise.reject(
        new Error('Amazon Connect Streams library not available')
      );
    }
    const connect = window.connect as unknown as {
      core: {
        getFrameMediaDevices: (timeout: number) => Promise<MediaDeviceInfo[]>;
      };
    };
    return connect.core.getFrameMediaDevices(timeoutMs);
  }

  /**
   * Fetch quick connect endpoints for the given queue ARNs via agent.getEndpoints.
   * This is the same list the CCP shows: quick connects associated with those queues.
   * Runs through the agent's CCP session, not the backend IAM role.
   */
  getQueueEndpoints(
    agent: ConnectAgent,
    queueARNs: string[],
    callbacks: {
      success: (data: { endpoints: ConnectQueueEndpoint[] }) => void;
      failure?: (err?: Error) => void;
    }
  ): void {
    if (!queueARNs?.length || !agent.getEndpoints) {
      callbacks.failure?.(new Error('Queue endpoints not available'));
      return;
    }
    agent.getEndpoints(queueARNs, {
      success: (data: unknown) => {
        callbacks.success({ endpoints: normalizeGetEndpointsResponse(data) });
      },
      failure: callbacks.failure,
    });
  }

  /**
   * Create an outbound contact to the given E.164 phone number.
   * Uses agent.connect(Endpoint.byPhoneNumber(phoneNumber), { queueARN?, success, failure }).
   * Only calls if agent has outboundCall permission. Optionally passes defaultOutboundQueue.
   */
  connectOutbound(
    phoneNumber: string,
    callbacks: ConnectCallback,
    options?: { queueARN?: string }
  ): void {
    if (typeof window === 'undefined' || !window.connect) {
      callbacks.failure?.(
        new Error('Amazon Connect Streams library not available')
      );
      return;
    }
    const connect = window.connect as unknown as ConnectGlobal;
    const agent = new connect.Agent() as ConnectAgent;
    const permissions = agent.getPermissions?.();
    if (permissions && !permissions.includes('outboundCall')) {
      callbacks.failure?.(
        new Error('Agent does not have outbound call permission')
      );
      return;
    }
    const queueARN =
      options?.queueARN ??
      (
        agent.getRoutingProfile?.()?.defaultOutboundQueue as
          | { queueARN?: string }
          | undefined
      )?.queueARN ??
      agent.getAllQueueARNs?.()?.[0];
    const endpoint = connect.Endpoint.byPhoneNumber(phoneNumber);
    this.markAgentConnectOutboundDialStarted();
    const connectOpts = {
      ...callbacks,
      queueARN,
      failure: (err?: Error) => {
        if (!this.agentConnectOutboundContactIds.size) {
          this.agentConnectOutboundPending = false;
          if (this.agentConnectOutboundPendingTimer) {
            clearTimeout(this.agentConnectOutboundPendingTimer);
            this.agentConnectOutboundPendingTimer = null;
          }
        }
        callbacks.failure?.(err);
      },
    } as {
      queueARN?: string;
      success?: () => void;
      failure?: (err?: Error) => void;
    };
    agent.connect?.(endpoint, connectOpts);
  }

  /**
   * Place the customer leg on hold for warm transfer (initial connection).
   */
  startWarmTransferHold(
    contact: ConnectContact,
    callbacks: ConnectCallback
  ): void {
    const holdConnection = getHoldResumeConnection(contact);
    if (!holdConnection?.hold) {
      callbacks.failure?.(new Error('Hold not available on this contact'));
      return;
    }
    holdConnection.hold(callbacks);
  }

  /**
   * Add a consult connection without destroying the agent leg (warm transfer).
   */
  addWarmTransferConsult(
    contact: ConnectContact,
    endpoint: ConnectQueueEndpoint,
    callbacks: ConnectCallback
  ): void {
    addContactConnection(contact, endpoint, callbacks);
  }

  /**
   * Resume the customer (initial) connection for a 3-way conference. When the
   * customer is already off hold (e.g. the receiving agent already joined
   * them) the operation is a no-op instead of tearing the transfer down.
   */
  conferenceWarmTransfer(
    contact: ConnectContact,
    callbacks: ConnectCallback
  ): void {
    const resumeConnection = getHoldResumeConnection(contact);
    if (!resumeConnection?.resume) {
      callbacks.failure?.(new Error('Resume not available on this contact'));
      return;
    }
    if (resumeConnection.isOnHold?.() === false) {
      callbacks.success?.();
      return;
    }
    resumeConnection.resume({
      success: callbacks.success,
      failure: (err?: Error) => {
        // Already resumed (e.g. receiving agent brought the customer back) is
        // the desired end state — treat as success, don't tear the transfer down.
        if (isCallAlreadyResumed(err)) {
          callbacks.success?.();
          return;
        }
        console.error('[WarmTransfer] resumeConnection failed', err);
        callbacks.failure?.(err);
      },
    });
  }

  /**
   * Complete warm transfer: remove Agent 1 leg only; customer + consult remain.
   */
  completeWarmTransfer(
    contact: ConnectContact,
    callbacks: ConnectCallback
  ): void {
    const agentConnection = contact.getAgentConnection?.();
    if (!agentConnection?.destroy) {
      callbacks.failure?.(new Error('Agent connection not available'));
      return;
    }
    agentConnection.destroy(callbacks);
  }

  /**
   * Cancel warm transfer consult: destroy a specific non-initial connection.
   * Legs Connect has already ended are skipped — the CCP rejects destroy on
   * them, and there is nothing left to cancel.
   */
  cancelWarmTransferConsult(
    contact: ConnectContact,
    consultConnectionId: string | null,
    callbacks: ConnectCallback
  ): void {
    const target = findThirdPartyConnection(contact, {
      connectionId: consultConnectionId,
      legFilter: 'outbound_or_queue',
      excludeEndedLegs: true,
    });
    if (!target?.destroy) {
      callbacks.success?.();
      return;
    }
    target.destroy(callbacks);
  }

  /**
   * Transfer contact to a phone number (cold transfer).
   * Adds connection to the phone endpoint, then destroys the agent connection
   * so the call returns to the phone number.
   */
  transferToPhone(
    contact: ConnectContact,
    phoneNumber: string,
    callbacks: ConnectCallback
  ): void {
    if (typeof window === 'undefined' || !window.connect) {
      callbacks.failure?.(
        new Error('Amazon Connect Streams library not available')
      );
      return;
    }
    const connect = window.connect as unknown as ConnectGlobal;
    const endpoint = connect.Endpoint.byPhoneNumber(phoneNumber);
    addContactConnection(contact, endpoint as ConnectQueueEndpoint, callbacks, {
      destroyAgentConnectionOnSuccess: true,
    });
  }

  /**
   * Transfer contact to a queue (cold transfer).
   * Adds connection to the queue endpoint, then destroys the agent connection
   * so the call goes to the queue.
   */
  transferToQueue(
    contact: ConnectContact,
    queueEndpoint: ConnectQueueEndpoint,
    callbacks: ConnectCallback
  ): void {
    addContactConnection(contact, queueEndpoint, callbacks, {
      destroyAgentConnectionOnSuccess: true,
    });
  }

  /**
   * Returns true if the contact is a chat contact (Streams ContactType.CHAT).
   */
  isChatContact(contact: ConnectContact | null): boolean {
    if (!contact?.getType || typeof window === 'undefined' || !window.connect)
      return false;
    try {
      const connect = window.connect as unknown as {
        ContactType?: { CHAT?: unknown };
      };
      return (
        connect.ContactType?.CHAT !== undefined &&
        contact.getType() === connect.ContactType.CHAT
      );
    } catch {
      return false;
    }
  }

  /** Agent connection for a CHAT contact (Streams ConnectionType.AGENT). */
  getAgentChatConnection(contact: ConnectContact): ConnectConnection {
    if (typeof window === 'undefined' || !window.connect) {
      throw new Error('Amazon Connect Streams library not available');
    }
    const fromList = contact
      .getConnections()
      .find(conn => conn.getType?.() === 'agent');
    if (!fromList) {
      throw new Error('Chat contact has no agent connection');
    }
    return fromList;
  }

  /**
   * ChatJS agent session via connection.getMediaController() (Streams + ChatJS docs).
   * Used to read the connection token for GET /chat/init and to send agent messages.
   * Falls back to connect.core.mediaFactory.get(connection) when needed.
   */
  async getChatMediaController(
    contact: ConnectContact | null
  ): Promise<ConnectChatController> {
    if (!contact || !this.isChatContact(contact)) {
      return Promise.reject(new Error('Contact is not a chat contact'));
    }
    const agentConnection = this.getAgentChatConnection(contact);
    if (typeof agentConnection.getMediaController === 'function') {
      return agentConnection.getMediaController();
    }
    if (typeof window === 'undefined' || !window.connect) {
      return Promise.reject(
        new Error('Amazon Connect Streams library not available')
      );
    }
    const connect = window.connect as unknown as {
      core: {
        mediaFactory?: {
          get: (conn: unknown) => Promise<ConnectChatController>;
        };
      };
    };
    const factory = connect.core?.mediaFactory;
    if (!factory?.get) {
      return Promise.reject(new Error('Connect media factory not available'));
    }
    return factory.get(agentConnection);
  }

  /**
   * After chat accept: ensure ChatJS session has connection credentials, read
   * connectionDetails.connectionToken, then GET /chat/init with it.
   */
  async handleAgentChatAccepted(contact: ConnectContact): Promise<void> {
    if (!this.isChatContact(contact)) return;

    const contactId = this.getCallScopeContactId(contact);

    try {
      const session = await this.getChatMediaController(contact);
      const connectionToken =
        typeof session.getChatDetails === 'function'
          ? session.getChatDetails().connectionDetails?.connectionToken
          : undefined;
      if (connectionToken) {
        await chatService.initiateChat(contactId, connectionToken);
      }
    } catch (error) {
      console.log('[Connect][handleAgentChatAccepted] error', error);
    }
  }

  /**
   * Sends a text message in a chat contact. Uses the ChatJS sendMessage API:
   * sendMessage({ contentType: "text/plain", message: content }).
   */
  sendChatMessage(
    contact: ConnectContact | null,
    content: string,
    callbacks?: { success?: () => void; failure?: (err?: Error) => void }
  ): void {
    this.getChatMediaController(contact)
      .then(controller => {
        const send = controller.sendMessage;
        if (typeof send !== 'function') {
          callbacks?.failure?.(new Error('Chat controller has no sendMessage'));
          return;
        }
        return Promise.resolve(
          send({ contentType: 'text/plain', message: content })
        ).then(
          () => callbacks?.success?.(),
          err =>
            callbacks?.failure?.(
              err instanceof Error ? err : new Error(String(err))
            )
        );
      })
      .catch(err =>
        callbacks?.failure?.(
          err instanceof Error ? err : new Error(String(err))
        )
      );
  }
}

export const amazonConnectService = new AmazonConnectService();
